import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ArticleDesk, {
    DeskRibbon,
    DeskPanel,
    DeskTitle,
    DeskNote,
    DeskField,
    DESK_INPUT_CLASS,
} from '../../../components/staff/articles/ArticleDesk';

const API_URL = process.env.REACT_APP_API_URL;

function NewObserver({ userData }) {
    const today = new Date().toISOString().split('T')[0];

    const [formData, setFormData] = useState({
        title: '',
        publicDate: today,
        coverImageUrl: '',
    });
    const [pdfFile, setPdfFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);

    if (!userData || !userData.hasStaffRole) {
        return <Navigate to="/" replace />;
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (file) => {
        if (!file) return;
        if (file.type !== 'application/pdf') {
            toast.error('Only PDF files are allowed');
            return;
        }
        if (file.size > 20 * 1024 * 1024) {
            toast.error('PDF must be under 20MB');
            return;
        }
        setPdfFile(file);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        handleFileChange(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!pdfFile) {
            toast.error('Please select a PDF file');
            return;
        }

        if (!formData.title.trim()) {
            toast.error('Title is required');
            return;
        }

        if (!formData.publicDate) {
            toast.error('Publication date is required');
            return;
        }

        setUploading(true);

        const body = new FormData();
        body.append('pdf', pdfFile);
        body.append('title', formData.title.trim());
        body.append('publicDate', formData.publicDate);
        if (formData.coverImageUrl.trim()) {
            body.append('coverImageUrl', formData.coverImageUrl.trim());
        }

        try {
            const response = await fetch(`${API_URL}observer/issues`, {
                method: 'POST',
                credentials: 'include',
                body,
            });

            const data = await response.json();

            if (data.success) {
                toast.success(data.message || 'Issue uploaded successfully');
                setFormData({ title: '', publicDate: today, coverImageUrl: '' });
                setPdfFile(null);
            } else {
                toast.error(data.message || 'Upload failed');
            }
        } catch {
            toast.error('Could not connect to server');
        } finally {
            setUploading(false);
        }
    };

    return (
        <ArticleDesk userData={userData}>
            <DeskRibbon>
                <DeskTitle>New Observer Issue</DeskTitle>
                <DeskNote>Issue numbers are assigned automatically</DeskNote>

                <div className="flex-1" />

                <button
                    type="submit"
                    form="observer-form"
                    disabled={uploading}
                    className="px-4 py-[6px] font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                    style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.3)', backgroundColor: '#5A2929' }}
                >
                    {uploading ? 'Uploading...' : 'Upload Issue'}
                </button>
            </DeskRibbon>

            <form id="observer-form" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 items-start gap-4 min-[1360px]:grid-cols-[minmax(0,1fr)_400px]">
                    <DeskPanel title="PDF File">
                        <div
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={handleDrop}
                            className={`cursor-pointer rounded border-2 border-dashed p-10 text-center transition-colors ${
                                dragOver
                                    ? 'border-maroon bg-red-50'
                                    : pdfFile
                                        ? 'border-green-400 bg-green-50'
                                        : 'border-gray-300 hover:border-maroon'
                            }`}
                            onClick={() => document.getElementById('pdf-input').click()}
                        >
                            <input
                                id="pdf-input"
                                type="file"
                                accept="application/pdf"
                                className="hidden"
                                onChange={(e) => handleFileChange(e.target.files[0])}
                            />
                            {pdfFile ? (
                                <div>
                                    <p className="font-['Jersey_10'] text-[17px] uppercase tracking-[1px] text-green-700">
                                        ✓ {pdfFile.name}
                                    </p>
                                    <p className="mt-1 text-[12px] text-green-600">
                                        {(pdfFile.size / 1024 / 1024).toFixed(2)} MB
                                    </p>
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setPdfFile(null); }}
                                        className="mt-3 font-['Jersey_10'] text-[12px] uppercase tracking-[1px] text-red-600 hover:text-red-800"
                                    >
                                        Remove
                                    </button>
                                </div>
                            ) : (
                                <div>
                                    <p className="font-['Jersey_10'] text-[17px] uppercase tracking-[1px] text-gray-500">
                                        Drag and drop a PDF here
                                    </p>
                                    <p className="mt-1 text-[12px] text-gray-400">or click to browse, max 20MB</p>
                                </div>
                            )}
                        </div>
                    </DeskPanel>

                    <DeskPanel title="Issue Details">
                        <div className="space-y-3">
                            <DeskField label="Issue Title" required>
                                <input
                                    id="title"
                                    name="title"
                                    type="text"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    placeholder="e.g. The Origins Observer"
                                    className={DESK_INPUT_CLASS}
                                    required
                                />
                            </DeskField>

                            <DeskField label="Publication Date" required>
                                <input
                                    id="publicDate"
                                    name="publicDate"
                                    type="date"
                                    value={formData.publicDate}
                                    onChange={handleInputChange}
                                    className={DESK_INPUT_CLASS}
                                    required
                                />
                            </DeskField>

                            <DeskField label="Cover Image URL" hint="optional">
                                <input
                                    id="coverImageUrl"
                                    name="coverImageUrl"
                                    type="url"
                                    value={formData.coverImageUrl}
                                    onChange={handleInputChange}
                                    placeholder="https://..."
                                    className={DESK_INPUT_CLASS}
                                />
                            </DeskField>

                            <p className="text-[11px] text-gray-500">
                                The cover image is used as the thumbnail in the carousel spotlight.
                            </p>
                        </div>
                    </DeskPanel>
                </div>
            </form>
        </ArticleDesk>
    );
}

export default NewObserver;
